import { ChangeDetectionStrategy, Component, ElementRef, computed, effect, inject, input, model, output, DOCUMENT } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { SharedModule } from '@shared/shared.module';
import {
  HD_ACTIVE_GATE_COLOR_PRESET_OPTIONS,
  HD_ADVANCED_BRIDGE_OPTIONS,
  HD_BASE_ROTATION_OPTIONS,
  HD_BENEATH_THE_LINE_STYLE_OPTIONS,
  HD_BRIDGE_GATE_STYLE_OPTIONS,
  HD_CENTER_BORDER_STYLE_OPTIONS,
  HD_CHIRON_DISPLAY_OPTIONS,
  HD_CONTENT_LANGUAGE_OPTIONS,
  HD_GATE_INFO_OPEN_BEHAVIOR_OPTIONS,
  HD_GATE_NUMBER_FONT_SIZE_OPTIONS,
  HD_JUST_NOW_TRANSIT_COLOR_MODE_OPTIONS,
  HD_MANDALA_QUARTER_COLOR_VARIANT_OPTIONS,
  HD_MANDALA_SLICE_CENTER_FADE_OPTIONS,
  HD_MANDALA_ZODIAC_RING_VARIANT_OPTIONS,
  HD_MOON_TRANSIT_CONNECTOR_STYLE_OPTIONS,
  HD_MOON_TRANSIT_GATE_MARKER_STYLE_OPTIONS,
  HD_NODE_BACKGROUND_STYLE_OPTIONS,
  HD_PLANET_GRID_VISUALIZATION_MODE_OPTIONS,
  HD_UNDEFINED_STYLE_OPTIONS,
  HdBridgeGateStyle,
} from '../hd-chart-controls-options';
import { HdPlanetGridVisualizationMode } from '../hd-bodygraph.models';
import { HdChartButtonSize } from './hd-chart-shortcut-bar.component';
import { HdChartMatchScope } from './hd-chart-global-settings';
import { HdMoonTransitConnectorStyle, HdMoonTransitGateMarkerStyle } from '../../mandala/moon-transit/hd-moon-transit-view.models';
import { HdJustNowTransitColorMode } from '../../mandala/just-now-transit/hd-just-now-transit';
import { HdMandalaQuarterColorVariant, HdMandalaZodiacRingVariant } from '../../mandala/mandala.constants';
import {
  HD_BODYGRAPH_COLOR_CONFIG_PRESETS,
  HdBodygraphColorConfig,
  HdBodygraphColorConfigPresetId,
} from '../hd-bodygraph-color-config';
import {
  HD_PLANET_GRID_COLOR_CONFIG_PRESETS,
  HdPlanetGridColorConfig,
  HdPlanetGridColorConfigPresetId,
} from '../planet-grid/hd-planet-grid-color-config';
import { HdGateInfoOpenBehavior } from '../gate-info/hd-gate-info-requested-context.model';
import {
  HD_GATE_GRAPH_PALETTES,
  HD_GATE_GRAPH_PALETTE_DEFAULT,
  HdGateGraphPaletteId,
} from '../gate-graph/hd-gate-graph-palette';
import { HD_CONTENT_LANGUAGES, HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguage, HdContentLanguageService } from '../hd-content-language.service';
import {
  HD_DOC_LINK_OPEN_BEHAVIOR_OPTIONS,
  HdDocLinkOpenBehavior,
} from '../doc-panel/hd-doc-link-open-behavior';
import { PREFERRED_LANGUAGE_STORAGE_KEY } from '@/app/core/pages/public-account/language-selector/language-selector.component';
import {
  HD_HOVER_HIGHLIGHT_COLORS,
  HD_HOVER_HIGHLIGHT_COLOR_DEFAULT,
  HdHoverHighlightColor,
} from '../hd-hover-highlight-theme';

/**
 * Rij-descriptor voor de visualisatie-sectie: alle stijl-dropdowns worden
 * data-driven gerenderd (label + opties + huidige id) zodat de template één
 * @for-lus is i.p.v. negen gedupliceerde dropdown-blokken.
 */
interface HdVisualizationRow {
  readonly key: string;
  readonly label: string;
  readonly options: ReadonlyArray<{ readonly id: string; readonly label: string }>;
  readonly selectedId: string;
}

@Component({
  selector: 'hd-chart-settings-panel',
  imports: [SharedModule],
  templateUrl: './hd-chart-settings-panel.component.html',
  styleUrl: './hd-chart-settings-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdChartSettingsPanelComponent {
  /** Two-way open-state; de pagina persisteert dit in de globale settings. */
  readonly open = model(false);

  // Visualisatie (globaal)
  readonly colorConfig = input.required<HdBodygraphColorConfig>();
  readonly colorConfigPresetId = input<HdBodygraphColorConfigPresetId>('warm-dusk');
  readonly planetGridColorConfig = input.required<HdPlanetGridColorConfig>();
  readonly planetGridColorConfigPresetId = input<HdPlanetGridColorConfigPresetId>('warm-dusk-subtle');
  readonly planetGridVisualizationMode = input<HdPlanetGridVisualizationMode>('extended');
  readonly showChiron = input(true);
  readonly showAstroPositions = input(false);
  readonly includeNonHdFeatures = input(true);

  // Interactie (globaal)
  readonly gateInfoOpenBehavior = input<HdGateInfoOpenBehavior>('full');
  /** Kleurenschema van de radiale poort-visualisatie. */
  readonly gateGraphPalette = input<HdGateGraphPaletteId>(HD_GATE_GRAPH_PALETTE_DEFAULT);
  readonly docLinkOpenBehavior = input<HdDocLinkOpenBehavior>('same-panel');
  readonly contextMenuEnabled = input(true);
  readonly channelHighlightEnabled = input(true);
  /** Dimmen bij hover; `false` = enkel blauwe-rand-highlights, niets dimt. */
  readonly dimOnHover = input(true);
  /** Kleur van de hover-highlightranden. */
  readonly hoverHighlightColor = input<HdHoverHighlightColor>(HD_HOVER_HIGHLIGHT_COLOR_DEFAULT);
  /** Outline rond het kanaalstuk van gehighlighte poorten (experiment). */
  readonly highlightGatePieces = input(false);
  /** Enkel de kant tegenover de hover-bron dimmen; enkel zinvol met dimOnHover. */
  readonly dimOneSideOnly = input(false);
  /** Basis-paneel (center- en activatie-kaartjes) gekoppeld aan de chart-hover; zwaar, default uit. */
  readonly basicPanelChartLink = input(false);
  /** Community-content (Line Companion e.d.) tonen; opt-in, default uit. */
  readonly communityContentEnabled = input(false);
  /** Alternatieve I Tjing-interpretaties (Book of Lines) tonen; opt-in, default uit. */
  readonly alternativeIChingEnabled = input(false);
  /** Ra-lijnteksten (blauwe lijnbeschrijving + White/Black Book) tonen; default aan. */
  readonly raLineTextsEnabled = input(true);
  /** Eigen lijnteksten (Mechanics in Motion) tonen; opt-in, default uit. */
  readonly ownLineTextsEnabled = input(false);
  /** UPAM-feature ReviewLineTexts toegekend: toont de knop naar het reviewoverzicht. */
  readonly lineTextReviewFeatureGranted = input(false);
  /** Classic I Ching (Wilhelm NL / Legge EN) tonen; opt-in, default uit. */
  readonly classicIChingEnabled = input(false);
  /** UPAM-feature Classic I Ching toegekend: zonder feature bestaat de sectie niet. */
  readonly classicIChingFeatureGranted = input(false);
  /** Classic I Ching-doorklikiconen bij de chart (Geavanceerd, composite). */
  readonly classicIChingIconsChart = input(true);
  /** Classic I Ching-doorklikiconen bij transits (kalender, Just Now, ephemeris). */
  readonly classicIChingIconsTransits = input(true);
  readonly keynotesAtHoverOnly = input(true);
  readonly showBothCenterStateSections = input(true);
  readonly gateInfoFromChannelSeparateToDock = input(true);
  readonly advancedBridgeCalculation = input(true);
  readonly bridgeGateStyle = input<HdBridgeGateStyle>('badge');
  /** Verbindingslijnen in de maan-transit modus (spec 2026-07-06 §6.5). */
  readonly moonTransitConnectorStyle = input<HdMoonTransitConnectorStyle>('curved');
  /** Markering van de maanpoorten in het wiel (finetune-ronde 3). */
  readonly moonTransitGateMarkerStyle = input<HdMoonTransitGateMarkerStyle>('ring');
  readonly justNowTransitColorMode = input<HdJustNowTransitColorMode>('three-color');
  /** Kleurvariant van de mandala-kwartaalband (A/B-vergelijk). */
  readonly mandalaQuarterColorVariant = input<HdMandalaQuarterColorVariant>('soft');
  /** Dierenriem-band: donker (default) of licht. */
  readonly mandalaZodiacRingVariant = input<HdMandalaZodiacRingVariant>('dark');
  /** Gefadede centerkleur in de mandala-slices van geactiveerde poorten (default aan). */
  readonly mandalaSliceCenterFade = input(true);
  readonly shortcutButtonSize = input<HdChartButtonSize>('sm');
  /** Scope van de "gelijkaardige charts"-matchlijsten (bibliotheek of enkel gerelateerd). */
  readonly chartMatchScope = input<HdChartMatchScope>('library');
  /** Toont de print-knop enkel wanneer er een chart is om te printen (app-brede host). */
  readonly showPrint = input(true);

  // Outputs — namen identiek aan chart-presets waar van toepassing
  readonly colorConfigPresetChange = output<HdBodygraphColorConfigPresetId>();
  readonly planetGridColorConfigPresetChange = output<HdPlanetGridColorConfigPresetId>();
  readonly visualizationStyleChange = output<{ key: string; optionId: string }>();
  readonly gateInfoOpenBehaviorChange = output<HdGateInfoOpenBehavior>();
  readonly gateGraphPaletteChange = output<HdGateGraphPaletteId>();
  readonly docLinkOpenBehaviorChange = output<HdDocLinkOpenBehavior>();
  readonly contextMenuEnabledToggled = output<void>();
  readonly channelHighlightToggled = output<void>();
  readonly dimOnHoverToggled = output<void>();
  readonly hoverHighlightColorChange = output<HdHoverHighlightColor>();
  readonly highlightGatePiecesToggled = output<void>();
  readonly dimOneSideOnlyToggled = output<void>();
  readonly basicPanelChartLinkToggled = output<void>();
  readonly communityContentEnabledToggled = output<void>();
  readonly alternativeIChingEnabledToggled = output<void>();
  readonly raLineTextsEnabledToggled = output<void>();
  readonly ownLineTextsEnabledToggled = output<void>();
  /** Vraag om het reviewoverzicht van de eigen lijnteksten te openen. */
  readonly lineTextReviewRequested = output<void>();
  readonly classicIChingEnabledToggled = output<void>();
  readonly classicIChingIconsChartToggled = output<void>();
  readonly classicIChingIconsTransitsToggled = output<void>();
  readonly keynotesAtHoverOnlyToggled = output<void>();
  readonly showBothCenterStateSectionsToggled = output<void>();
  readonly gateInfoFromChannelSeparateToDockToggled = output<void>();
  readonly includeNonHdFeaturesToggled = output<void>();
  readonly shortcutButtonSizeChange = output<HdChartButtonSize>();
  readonly chartMatchScopeChange = output<HdChartMatchScope>();
  readonly printRequested = output<void>();
  /** Onderaan het paneel: alle settings terug naar de app-defaults. */
  readonly resetToDefaultsRequested = output<void>();

  protected readonly gateInfoOpenBehaviorOptions = computed(() => this.resolveOptions(HD_GATE_INFO_OPEN_BEHAVIOR_OPTIONS));
  protected readonly gateGraphPaletteOptions = computed(() => this.resolveOptions(
    HD_GATE_GRAPH_PALETTES.map(palette => ({ id: palette.id, labelKey: palette.labelKey })),
  ));
  protected readonly docLinkOpenBehaviorOptions = computed(() => this.resolveOptions(HD_DOC_LINK_OPEN_BEHAVIOR_OPTIONS));
  protected readonly contentLanguageOptions = HD_CONTENT_LANGUAGE_OPTIONS;
  protected readonly hoverHighlightColors = HD_HOVER_HIGHLIGHT_COLORS;
  protected readonly colorConfigPresets = HD_BODYGRAPH_COLOR_CONFIG_PRESETS;
  protected readonly planetGridColorConfigPresets = HD_PLANET_GRID_COLOR_CONFIG_PRESETS;

  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly lang = computed(() => this.contentLang.effectiveLang(), HD_LANG_RECOMPUTE_ON_LOAD);
  private readonly hostElement = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  private readonly document = inject(DOCUMENT);

  constructor() {
    // Klik buiten het paneel (en buiten de handle) schuift het paneel dicht.
    // Zowel de handle als de aside leven binnen het host-element, dus een klik
    // erbinnen (incl. de in-place dropdown-menu's) telt als "binnen". De
    // listener wordt enkel geregistreerd terwijl het paneel open is; de klik
    // die het paneel opende is dan al verwerkt, dus geen directe self-close.
    effect(onCleanup => {
      if (!this.open()) {
        return;
      }
      const onPointerDown = (event: PointerEvent): void => {
        const target = event.target as Node | null;
        if (target && this.hostElement.contains(target)) {
          return;
        }
        this.open.set(false);
      };
      this.document.addEventListener('pointerdown', onPointerDown, true);
      onCleanup(() => this.document.removeEventListener('pointerdown', onPointerDown, true));
    });
  }

  protected onContextMenuToggleClick(): void {
    this.contextMenuEnabledToggled.emit();
  }

  protected onChannelHighlightToggleClick(): void {
    this.channelHighlightToggled.emit();
  }

  protected onGateInfoFromChannelSeparateToDockToggleClick(): void {
    this.gateInfoFromChannelSeparateToDockToggled.emit();
  }

  protected readonly selectedGateGraphPaletteLabel = computed(
    () => this.gateGraphPaletteOptions().find(o => o.id === this.gateGraphPalette())?.label ?? '…',
  );

  protected readonly selectedGateInfoOpenBehaviorLabel = computed(
    () => this.gateInfoOpenBehaviorOptions().find(o => o.id === this.gateInfoOpenBehavior())?.label ?? '…',
  );

  protected readonly selectedDocLinkOpenBehaviorLabel = computed(
    () => this.docLinkOpenBehaviorOptions().find(o => o.id === this.docLinkOpenBehavior())?.label ?? '…',
  );

  protected readonly selectedContentLang = computed<HdContentLanguage>(() => {
    const effective = this.contentLang.effectiveLang();
    return HD_CONTENT_LANGUAGES.includes(effective as HdContentLanguage)
      ? (effective as HdContentLanguage)
      : 'nl';
  });
  protected readonly selectedContentLangLabel = computed(
    () => this.contentLanguageOptions.find(o => o.id === this.selectedContentLang())?.label ?? this.t('humanDesign.chart.controls.fallback.language'),
  );

  /** Eén rij per stijl-setting; `key` matcht de dispatch in de pagina (Task 10). */
  protected readonly visualizationRows = computed<readonly HdVisualizationRow[]>(() => [
    { key: 'colorConfigPreset', label: this.t('humanDesign.chart.controls.rows.colorConfigPreset'), options: this.colorConfigPresets, selectedId: this.colorConfigPresetId() },
    { key: 'planetGridColorConfigPreset', label: this.t('humanDesign.chart.controls.rows.planetGridColorConfigPreset'), options: this.planetGridColorConfigPresets, selectedId: this.planetGridColorConfigPresetId() },
    { key: 'centerBorderStyle', label: this.t('humanDesign.chart.controls.rows.centerBorderStyle'), options: this.resolveOptions(HD_CENTER_BORDER_STYLE_OPTIONS), selectedId: this.colorConfig().centerBorderStyle },
    { key: 'activeGateColorPreset', label: this.t('humanDesign.chart.controls.rows.activeGateColorPreset'), options: this.resolveOptions(HD_ACTIVE_GATE_COLOR_PRESET_OPTIONS), selectedId: this.colorConfig().activeGateColorPreset },
    { key: 'undefinedStyle', label: this.t('humanDesign.chart.controls.rows.undefinedStyle'), options: this.resolveOptions(HD_UNDEFINED_STYLE_OPTIONS), selectedId: this.planetGridColorConfig().undefinedStyle },
    { key: 'nodeBackgroundStyle', label: this.t('humanDesign.chart.controls.rows.nodeBackgroundStyle'), options: this.resolveOptions(HD_NODE_BACKGROUND_STYLE_OPTIONS), selectedId: this.planetGridColorConfig().nodeBackgroundStyle },
    { key: 'beneathTheLineStyle', label: this.t('humanDesign.chart.controls.rows.beneathTheLineStyle'), options: this.resolveOptions(HD_BENEATH_THE_LINE_STYLE_OPTIONS), selectedId: this.planetGridColorConfig().beneathTheLineStyle },
    { key: 'planetGridVisualizationMode', label: this.t('humanDesign.chart.controls.rows.planetGridVisualizationMode'), options: this.resolveOptions(HD_PLANET_GRID_VISUALIZATION_MODE_OPTIONS), selectedId: this.planetGridVisualizationMode() },
    { key: 'showChiron', label: this.t('humanDesign.chart.controls.rows.showChiron'), options: this.resolveOptions(HD_CHIRON_DISPLAY_OPTIONS), selectedId: this.showChiron() ? 'on' : 'off' },
    { key: 'showAstroPositions', label: this.t('humanDesign.chart.controls.rows.showAstroPositions'), options: this.resolveOptions(HD_CHIRON_DISPLAY_OPTIONS), selectedId: this.showAstroPositions() ? 'on' : 'off' },
    { key: 'gateNumberFontSize', label: this.t('humanDesign.chart.controls.rows.gateNumberFontSize'), options: this.resolveOptions(HD_GATE_NUMBER_FONT_SIZE_OPTIONS), selectedId: this.planetGridColorConfig().gateNumberFontSize },
    { key: 'baseRotation', label: this.t('humanDesign.chart.controls.rows.baseRotation'), options: this.resolveOptions(HD_BASE_ROTATION_OPTIONS), selectedId: this.planetGridColorConfig().baseRotation },
    { key: 'advancedBridgeCalculation', label: this.t('humanDesign.chart.controls.rows.advancedBridgeCalculation'), options: this.resolveOptions(HD_ADVANCED_BRIDGE_OPTIONS), selectedId: this.advancedBridgeCalculation() ? 'on' : 'off' },
    { key: 'bridgeGateStyle', label: this.t('humanDesign.chart.controls.rows.bridgeGateStyle'), options: this.resolveOptions(HD_BRIDGE_GATE_STYLE_OPTIONS), selectedId: this.bridgeGateStyle() },
    { key: 'moonTransitConnectorStyle', label: this.t('humanDesign.chart.controls.rows.moonTransitConnectorStyle'), options: this.resolveOptions(HD_MOON_TRANSIT_CONNECTOR_STYLE_OPTIONS), selectedId: this.moonTransitConnectorStyle() },
    { key: 'moonTransitGateMarkerStyle', label: this.t('humanDesign.chart.controls.rows.moonTransitGateMarkerStyle'), options: this.resolveOptions(HD_MOON_TRANSIT_GATE_MARKER_STYLE_OPTIONS), selectedId: this.moonTransitGateMarkerStyle() },
    { key: 'justNowTransitColorMode', label: this.t('humanDesign.chart.controls.rows.justNowTransitColorMode'), options: this.resolveOptions(HD_JUST_NOW_TRANSIT_COLOR_MODE_OPTIONS), selectedId: this.justNowTransitColorMode() },
    { key: 'mandalaQuarterColorVariant', label: this.t('humanDesign.chart.controls.rows.mandalaQuarterColorVariant'), options: this.resolveOptions(HD_MANDALA_QUARTER_COLOR_VARIANT_OPTIONS), selectedId: this.mandalaQuarterColorVariant() },
    { key: 'mandalaZodiacRingVariant', label: this.t('humanDesign.chart.controls.rows.mandalaZodiacRingVariant'), options: this.resolveOptions(HD_MANDALA_ZODIAC_RING_VARIANT_OPTIONS), selectedId: this.mandalaZodiacRingVariant() },
    { key: 'mandalaSliceCenterFade', label: this.t('humanDesign.chart.controls.rows.mandalaSliceCenterFade'), options: this.resolveOptions(HD_MANDALA_SLICE_CENTER_FADE_OPTIONS), selectedId: this.mandalaSliceCenterFade() ? 'on' : 'off' },
  ]);

  protected selectedRowLabel(row: HdVisualizationRow): string {
    return row.options.find(option => option.id === row.selectedId)?.label ?? '…';
  }

  protected t(key: string, params?: Record<string, unknown>): string {
    // Ook `translationsLoadedTick()` mee-lezen: bij de eerste taalwissel is de
    // JSON van de override-taal nog niet geladen, dus deze eerste vertaling
    // valt terug op de fallback. Zonder deze extra signal-dependency wordt de
    // template niet opnieuw geëvalueerd wanneer het bestand ná de wissel is
    // ingeladen (bug: eerste switch in het chart-settings-panel had geen
    // zichtbaar effect tot de gebruiker een cheat-sheet had bezocht, waardoor
    // de scope al gecached was).
    this.contentLang.translationsLoadedTick();
    return this.transloco.translate(key, params, this.lang());
  }

  /** Resolveert een (id, labelKey)-optielijst naar (id, label) in de settings-taal. */
  private resolveOptions<T extends string>(
    options: readonly { readonly id: T; readonly labelKey: string }[],
  ): ReadonlyArray<{ readonly id: T; readonly label: string }> {
    return options.map(option => ({ id: option.id, label: this.t(option.labelKey) }));
  }

  protected togglePanel(): void {
    this.open.update(value => !value);
  }

  protected onVisualizationRowChange(
    row: HdVisualizationRow,
    optionId: string,
    dropdown: { toggleMenu: (visible?: boolean) => void },
  ): void {
    dropdown.toggleMenu(false);
    if (row.key === 'colorConfigPreset') {
      this.colorConfigPresetChange.emit(optionId as HdBodygraphColorConfigPresetId);
      return;
    }
    if (row.key === 'planetGridColorConfigPreset') {
      this.planetGridColorConfigPresetChange.emit(optionId as HdPlanetGridColorConfigPresetId);
      return;
    }
    this.visualizationStyleChange.emit({ key: row.key, optionId });
  }

  /**
   * Wijzigt de globale app-taal (chrome én HD-content). Dit is het enige
   * taal-keuze-element binnen de ingelogde app, dus het stuurt de globale
   * Transloco-taal — net als de taalkeuze op het login-scherm — en bewaart de
   * keuze in localStorage zodat ze behouden blijft na herladen. Een eventuele
   * losse content-override (gezet vanaf een cheat-sheet) wordt gewist zodat de
   * hele app consistent in de gekozen taal staat.
   */
  protected onContentLanguageChange(
    language: HdContentLanguage,
    dropdown: { toggleMenu: (visible?: boolean) => void },
  ): void {
    dropdown.toggleMenu(false);
    if (!HD_CONTENT_LANGUAGES.includes(language)) {
      return;
    }
    this.contentLang.setOverride(null);
    this.transloco.setActiveLang(language);
    localStorage.setItem(PREFERRED_LANGUAGE_STORAGE_KEY, language);
  }
}
